import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/pages/about/components/ContactForm";
import { useSeo } from "@/hooks/useSeo";

const SCOPE_ITEMS = [
  {
    icon: "ri-movie-2-line",
    title: "电影资源",
    desc: "涵盖剧情、家庭、悬疑、科幻等多元电影类型，提供完整的剧情介绍与详情。",
  },
  {
    icon: "ri-tv-2-line",
    title: "电视剧集",
    desc: "都市、家庭、古装、悬疑等热播剧集，随时浏览剧集简介与相关信息。",
  },
  {
    icon: "ri-heart-3-line",
    title: "主题专题",
    desc: "围绕“年轻的母亲”等温暖主题，策划精选影视专题合集。",
  },
];

const STEPS = [
  { step: "01", title: "浏览与查找", desc: "通过导航、分类或搜索框，快速找到感兴趣的影视。" },
  { step: "02", title: "筛选与排序", desc: "按类型、年份、评分多维度筛选，并按最新、热门或评分排序。" },
  { step: "03", title: "查看剧情详情", desc: "进入详情页阅读完整剧情介绍、演职人员与相关推荐。" },
  { step: "04", title: "收藏感兴趣内容", desc: "点击收藏按钮保存喜欢的影视，状态在本地即时反馈。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 年轻的母亲在线",
    description:
      "了解年轻的母亲在线的网站定位与内容范围，本平台聚焦年轻母亲主题影视内容，同时涵盖电影、电视剧资源，提供剧情介绍与资源浏览体验。",
    keywords: "关于年轻的母亲在线,网站介绍,影视资源平台,内容范围,联系方式",
  });

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      {/* 网站定位 */}
      <section className="mt-6 rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
          <i className="ri-film-line"></i>
          关于我们
        </span>
        <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
          年轻的母亲在线
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose max-w-3xl">
          年轻的母亲在线是一个聚焦“年轻的母亲”主题影视内容的在线资源平台。我们相信，每一部关于母亲的影视作品，都藏着值得被认真讲述的温暖。因此，我们用清晰的结构与便捷的筛选，帮助你快速找到想看的电影与电视剧，并阅读完整的剧情介绍。
        </p>
        <p className="mt-3 text-sm md:text-base text-foreground-700 leading-loose max-w-3xl">
          除主题内容外，平台同样涵盖更广泛的电影、电视剧资源，满足不同观影需求。本站影视信息均为虚构示例数据，仅用于页面演示，不提供在线播放与下载功能。
        </p>
      </section>

      {/* 内容范围 */}
      <section className="mt-12">
        <SectionHeading icon="ri-stack-line" title="内容范围" subtitle="我们提供哪些影视内容" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SCOPE_ITEMS.map((item) => (
            <div
              key={item.title}
              className="rounded-lg bg-background-100 border border-background-200 p-5"
            >
              <span className="w-11 h-11 rounded-lg bg-accent-500 text-foreground-950 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 使用说明 */}
      <section className="mt-12">
        <SectionHeading icon="ri-guide-line" title="使用说明" subtitle="四步轻松找到想看的影视" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((item) => (
            <div
              key={item.step}
              className="rounded-lg bg-secondary-50 border border-secondary-100 p-5"
            >
              <span className="font-heading font-bold text-2xl text-secondary-400">
                {item.step}
              </span>
              <h3 className="mt-3 font-heading font-bold text-base text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <SectionHeading icon="ri-customer-service-2-line" title="联系我们" />
          <div className="rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
            <div className="flex items-start gap-3 p-4">
              <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-sm text-foreground-950 font-medium">邮箱</p>
                <p className="text-sm text-foreground-600">contact@ymm-online.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4">
              <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                <i className="ri-wechat-line"></i>
              </span>
              <div>
                <p className="text-sm text-foreground-950 font-medium">微信公众号</p>
                <p className="text-sm text-foreground-600">年轻的母亲在线</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4">
              <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-900 flex items-center justify-center shrink-0">
                <i className="ri-calendar-line"></i>
              </span>
              <div>
                <p className="text-sm text-foreground-950 font-medium">内容更新</p>
                <p className="text-sm text-foreground-600">每日持续上新影视资源</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <ContactForm />
        </div>
      </section>
    </div>
  );
}